import React, { useRef, useEffect, useMemo } from "react";
import { ReactBarcode } from "react-jsbarcode";

/**
 * Barcode128: código de barras Code 128 usando la librería react-jsbarcode
 * (envoltorio de JsBarcode). Mantiene la misma interfaz de props que se usaba
 * antes para no tener que cambiar el resto de la pantalla.
 *
 * Props:
 *  - value: cadena a codificar (ASCII imprimible)
 *  - height: alto de las barras en px (default 60)
 *  - narrow: ancho de un módulo/barra en px (default 1.6) -> opción `width` de JsBarcode
 *  - showText: muestra el texto legible debajo (default true)
 *  - padding: padding de referencia para la zona muda (default 10)
 *  - background: color de fondo (default #FFFFFF)
 *  - fontSize: tamaño del texto px (default 14)
 *  - widthCm / heightCm: tamaño físico impreso en cm (opcional). Cuando ambos son
 *      > 0, el SVG se fuerza a ese tamaño exacto (preserveAspectRatio="none") para
 *      imprimirse en las dimensiones configuradas.
 */
export default function Barcode128({
  value,
  height = 60,
  narrow = 1.6,
  showText = true,
  padding = 10,
  background = "#FFFFFF",
  fontSize = 14,
  widthCm = 0,
  heightCm = 0,
}) {
  const wrapRef = useRef(null);
  const safe = String(value == null ? "" : value);
  const physical = Number(widthCm) > 0 && Number(heightCm) > 0;
  // Zona muda: margen en blanco lateral de al menos 10x el módulo angosto para
  // que el escáner reconozca el código.
  const quiet = Math.max(padding, narrow * 12);

  const options = useMemo(
    () => ({
      format: "CODE128",
      width: narrow, // ancho de un módulo en px
      height, // alto de las barras en px
      displayValue: showText,
      fontSize,
      font: "monospace",
      textMargin: 4,
      margin: 0,
      marginLeft: quiet,
      marginRight: quiet,
      background,
      lineColor: "#000000",
    }),
    [narrow, height, showText, fontSize, quiet, background]
  );

  // react-jsbarcode (hijo) dibuja el SVG en su propio useEffect, que se ejecuta
  // antes que este. Aquí ajustamos el tamaño del SVG ya dibujado.
  useEffect(() => {
    const svg = wrapRef.current && wrapRef.current.querySelector("svg");
    if (!svg) return;
    if (physical) {
      svg.setAttribute("width", `${Number(widthCm)}cm`);
      svg.setAttribute("height", `${Number(heightCm)}cm`);
      svg.setAttribute("preserveAspectRatio", "none");
    } else {
      svg.setAttribute("width", "100%");
      svg.setAttribute("height", "auto");
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    }
    svg.style.display = "block";
    svg.style.maxWidth = "100%";
  });

  if (!safe) return null;

  return (
    <div
      ref={wrapRef}
      style={{ width: "100%", display: "flex", justifyContent: "center" }}
    >
      <ReactBarcode value={safe} options={options} renderer="svg" />
    </div>
  );
}
